import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { type ClassName, tw } from '@ctw/shared/utils/tailwind';
import { useMemo } from 'react';

interface VitalsListProps {
  vitalBucket: VitalsBucket;
  className?: ClassName;
  isLoading?: boolean;
}

export function VitalsList({ vitalBucket, className, isLoading }: VitalsListProps) {
  const vitalEntries = useMemo(
    () =>
      [
        {
          label: 'Blood Pressure',
          value: vitalBucket.bloodPressure?.display,
        },
        {
          label: 'Pulse',
          value: vitalBucket.pulse?.observation.valueWithoutUnits,
        },
        {
          label: 'Temperature',
          value: vitalBucket.temperature?.observation.valueWithUnits,
        },
        {
          label: 'Respiratory Rate',
          value: vitalBucket.respiratoryRate?.observation.valueWithoutUnits,
        },
        {
          label: 'Oxygen Saturation',
          value: vitalBucket.oxygenSaturation?.observation.valueWithoutUnits,
        },
        {
          label: 'Height/Length',
          value: vitalBucket.height?.observation.valueWithUnits,
        },
        {
          label: 'Weight',
          value: vitalBucket.weight?.observation.valueWithUnits,
        },
        {
          label: 'BMI',
          value: vitalBucket.bmi?.observation.valueWithoutUnits,
        },
        {
          label: 'Head Circumference',
          value: vitalBucket.headCircumference?.observation.valueWithoutUnits,
        },
        {
          label: 'LMP',
          value: vitalBucket.lmp?.observation.valueWithoutUnits,
        },
        {
          label: 'Pain',
          value: vitalBucket.pain?.observation.valueWithoutUnits,
        },
      ].filter((entry) => entry.value !== undefined),
    [vitalBucket],
  );

  if (isLoading) {
    return <LoadingSpinner centered={true} message="Loading vitals..." />;
  }

  if (vitalEntries.length === 0) {
    return <div className={tw`text-gray-500 italic`}>No vital signs available</div>;
  }

  return (
    <div className={tw`${className || ''}`}>
      <div className={tw`mb-2 flex border-gray-200 border-b font-bold text-gray-700`}>
        <div className={tw`flex-1 px-2 py-3`}>Vital</div>
        <div className={tw`flex-1 px-2 py-3`}>Result</div>
      </div>

      <div className={tw`flex flex-col`}>
        {vitalEntries.map(({ label, value }) => (
          <div key={label} className={tw`flex items-center border-gray-100 border-b last:border-0`}>
            <div className={tw`flex-1 px-2 py-3 text-gray-700`}>{label}</div>
            <div className={tw`flex-1 px-2 py-3 font-medium`}>{value?.toString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
